/** Hand-drawn style SVG illustrations for empty states. */

interface IllustrationProps {
  className?: string;
}

/** Stack of books with a bookmark sticking out — "No courses yet" */
export function BooksIllustration({ className }: IllustrationProps) {
  return (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      {/* Bottom book */}
      <rect x="20" y="70" width="80" height="14" rx="3" fill="var(--color-accent-soft)" stroke="var(--color-accent)" strokeWidth="1.5" />
      <rect x="24" y="73" width="30" height="2" rx="1" fill="var(--color-accent)" opacity="0.4" />
      {/* Middle book */}
      <rect x="25" y="54" width="75" height="14" rx="3" fill="var(--color-accent-2-soft)" stroke="var(--color-accent-2)" strokeWidth="1.5" />
      <rect x="29" y="57" width="25" height="2" rx="1" fill="var(--color-accent-2)" opacity="0.4" />
      {/* Top book */}
      <rect x="22" y="38" width="72" height="14" rx="3" fill="var(--color-mint-soft)" stroke="var(--color-mint)" strokeWidth="1.5" />
      <rect x="26" y="41" width="28" height="2" rx="1" fill="var(--color-mint)" opacity="0.4" />
      {/* Bookmark ribbon */}
      <path d="M78 38V22l5 4 5-4v16" fill="var(--color-celebration)" opacity="0.8" />
      <path d="M78 38V22l5 4 5-4v16" stroke="var(--color-celebration)" strokeWidth="1" fill="none" />
    </svg>
  );
}

/** Empty notebook with a feather pen — "No vocab yet" */
export function NotebookIllustration({ className }: IllustrationProps) {
  return (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      {/* Notebook */}
      <rect x="25" y="20" width="60" height="80" rx="4" fill="var(--color-bg-card)" stroke="var(--color-border)" strokeWidth="1.5" />
      {/* Spine rings */}
      {[32, 44, 56, 68, 80].map((y) => (
        <circle key={y} cx="25" cy={y} r="3" fill="none" stroke="var(--color-fg-3)" strokeWidth="1.2" />
      ))}
      {/* Lines on page */}
      {[36, 46, 56, 66, 76].map((y) => (
        <line key={y} x1="35" y1={y} x2="75" y2={y} stroke="var(--color-border)" strokeWidth="0.8" strokeDasharray="2 2" />
      ))}
      {/* Feather pen */}
      <path
        d="M90 18c-8 10-16 30-20 50l2 1c10-16 20-30 28-38"
        stroke="var(--color-accent-2)" strokeWidth="1.5" fill="var(--color-accent-2-soft)" strokeLinecap="round"
      />
      <path d="M72 68l-2 8 6-4z" fill="var(--color-fg-3)" />
    </svg>
  );
}

/** Open book without a marker — "No bookmarks yet" */
export function OpenBookIllustration({ className }: IllustrationProps) {
  return (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      {/* Left page */}
      <path
        d="M60 30C50 28 30 26 16 30v58c14-4 34-2 44 0z"
        fill="var(--color-bg-card)" stroke="var(--color-border)" strokeWidth="1.5"
      />
      {/* Right page */}
      <path
        d="M60 30c10-2 30-4 44 0v58c-14-4-34-2-44 0z"
        fill="var(--color-bg-card)" stroke="var(--color-border)" strokeWidth="1.5"
      />
      {/* Spine */}
      <line x1="60" y1="28" x2="60" y2="90" stroke="var(--color-border)" strokeWidth="1.5" />
      {/* Left text lines */}
      {[42, 52, 62, 72].map((y) => (
        <line key={`l${y}`} x1="24" y1={y} x2="52" y2={y} stroke="var(--color-border)" strokeWidth="0.8" />
      ))}
      {/* Right text lines */}
      {[42, 52, 62, 72].map((y) => (
        <line key={`r${y}`} x1="68" y1={y} x2="96" y2={y} stroke="var(--color-border)" strokeWidth="0.8" />
      ))}
    </svg>
  );
}

/** Rising sun — "Lesson complete" */
export function SunriseIllustration({ className }: IllustrationProps) {
  return (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      {/* Horizon line */}
      <line x1="10" y1="75" x2="110" y2="75" stroke="var(--color-border)" strokeWidth="1.5" />
      {/* Sun body (half circle) */}
      <path
        d="M35 75a25 25 0 0 1 50 0"
        fill="var(--color-celebration-soft)" stroke="var(--color-celebration)" strokeWidth="1.5"
      />
      {/* Rays */}
      {[
        'M60 42V32',
        'M42 48L35 40',
        'M78 48L85 40',
        'M30 65L20 60',
        'M90 65L100 60',
        'M38 55L28 48',
        'M82 55L92 48',
      ].map((d) => (
        <path key={d} d={d} stroke="var(--color-celebration)" strokeWidth="1.5" strokeLinecap="round" />
      ))}
      {/* Ground texture */}
      <path d="M10 80c20 4 40-2 50 2s30-1 40 0" stroke="var(--color-mint)" strokeWidth="1" opacity="0.4" />
      <path d="M10 88c15 2 35-3 45 1s35-2 40 1" stroke="var(--color-mint)" strokeWidth="1" opacity="0.3" />
    </svg>
  );
}

/** Steaming cup of tea — "Generic empty" / 404 */
export function TeaCupIllustration({ className }: IllustrationProps) {
  return (
    <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      {/* Saucer */}
      <ellipse cx="55" cy="95" rx="35" ry="6" fill="var(--color-bg-tint)" stroke="var(--color-border)" strokeWidth="1.2" />
      {/* Cup body */}
      <path
        d="M30 55h50v30c0 8-10 12-25 12S30 93 30 85z"
        fill="var(--color-accent-soft)" stroke="var(--color-accent)" strokeWidth="1.5"
      />
      {/* Handle */}
      <path
        d="M80 62c8 0 14 5 14 12s-6 12-14 12"
        stroke="var(--color-accent)" strokeWidth="1.5" fill="none"
      />
      {/* Tea surface */}
      <ellipse cx="55" cy="58" rx="22" ry="3" fill="var(--color-caution)" opacity="0.25" />
      {/* Steam */}
      <path d="M42 48c0-6 4-6 4-12" stroke="var(--color-fg-3)" strokeWidth="1.2" strokeLinecap="round" opacity="0.4">
        <animate attributeName="d" values="M42 48c0-6 4-6 4-12;M42 48c0-6-4-6-4-12;M42 48c0-6 4-6 4-12" dur="3s" repeatCount="indefinite" />
      </path>
      <path d="M52 45c0-6 4-6 4-12" stroke="var(--color-fg-3)" strokeWidth="1.2" strokeLinecap="round" opacity="0.3">
        <animate attributeName="d" values="M52 45c0-6 4-6 4-12;M52 45c0-6-4-6-4-12;M52 45c0-6 4-6 4-12" dur="2.5s" repeatCount="indefinite" />
      </path>
      <path d="M62 48c0-6 4-6 4-12" stroke="var(--color-fg-3)" strokeWidth="1.2" strokeLinecap="round" opacity="0.35">
        <animate attributeName="d" values="M62 48c0-6 4-6 4-12;M62 48c0-6-4-6-4-12;M62 48c0-6 4-6 4-12" dur="3.5s" repeatCount="indefinite" />
      </path>
    </svg>
  );
}
